"use client"

import { useState, useEffect, useRef, memo } from "react"

const LOADING_MESSAGES = [
  "Calculating drop trajectory...",
  "Warming up the warp drive...",
  "Pressurizing the drop pod...",
  "Scanning for hostile lifeforms...",
  "Calibrating orbital sensors...",
  "Stabilizing pocket dimensions...",
  "Defragmenting the void...",
  "Awakening dormant AI...",
  "Charging plasma cores...",
  "Decrypting alien signals...",
  "Venting excess radiation...",
  "Synchronizing neural uplink...",
  "Plotting extraction routes...",
  "Ignoring safety protocols...",
  "Summoning assets from the abyss...",
  "Consulting the ancient shaders...",
  "Attuning mana crystals...",
  "Negotiating with dungeon spirits...",
  "Compiling spell matrices...",
  "Feeding the code goblins...",
  "Bribing the render gods...",
  "Sharpening procedural swords...",
  "Brewing potion buffers...",
  "Disturbing ancient tombs...",
  "Waking the tower guardians...",
  "Binding elemental cores...",
  "Invoking the RNG gods...",
  "Rolling for initiative...",
  "Looting the texture cache...",
  "Reticulating dungeon splines...",
]

interface LoadingOverlayProps {
  progress: number // 0-100
  isComplete: boolean
  minDelayMs?: number // Only show if loading takes longer than this (default 200)
}

export const LoadingOverlay = memo(function LoadingOverlay({
  progress,
  isComplete,
  minDelayMs = 200,
}: LoadingOverlayProps) {
  const [currentMessage, setCurrentMessage] = useState("")
  const [displayedText, setDisplayedText] = useState("")
  const [isTyping, setIsTyping] = useState(true) // true = typing in, false = typing out
  const [shouldShow, setShouldShow] = useState(false)
  const messageIndexRef = useRef(0)
  const charIndexRef = useRef(0)

  // Delay showing the overlay
  useEffect(() => {
    if (isComplete) {
      setShouldShow(false)
      return
    }

    const timer = setTimeout(() => {
      if (!isComplete) {
        setShouldShow(true)
      }
    }, minDelayMs)

    return () => clearTimeout(timer)
  }, [isComplete, minDelayMs])

  // Initialize with random message
  useEffect(() => {
    messageIndexRef.current = Math.floor(Math.random() * LOADING_MESSAGES.length)
    setCurrentMessage(LOADING_MESSAGES[messageIndexRef.current])
  }, [])

  // Typewriter effect
  useEffect(() => {
    if (!shouldShow || isComplete) return

    const targetText = currentMessage
    const typeSpeed = 25 // ms per character for typing in
    const eraseSpeed = 15 // ms per character for erasing (faster)
    const pauseDuration = 3000 // 3 seconds between messages

    const interval = setInterval(() => {
      if (isTyping) {
        // Typing in
        if (charIndexRef.current < targetText.length) {
          charIndexRef.current++
          setDisplayedText(targetText.slice(0, charIndexRef.current))
        } else {
          // Finished typing, pause then start erasing
          clearInterval(interval)
          setTimeout(() => {
            setIsTyping(false)
          }, pauseDuration)
        }
      } else {
        // Erasing
        if (charIndexRef.current > 0) {
          charIndexRef.current--
          setDisplayedText(targetText.slice(0, charIndexRef.current))
        } else {
          // Finished erasing, move to next message
          clearInterval(interval)
          messageIndexRef.current = (messageIndexRef.current + 1) % LOADING_MESSAGES.length
          setCurrentMessage(LOADING_MESSAGES[messageIndexRef.current])
          setIsTyping(true)
        }
      }
    }, isTyping ? typeSpeed : eraseSpeed)

    return () => clearInterval(interval)
  }, [shouldShow, isComplete, currentMessage, isTyping])

  // Don't render if loading is complete or hasn't hit delay threshold
  if (isComplete || !shouldShow) return null

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/90 backdrop-blur-sm">
      {/* Container for progress bar and text - left aligned */}
      <div className="flex flex-col items-start">
        {/* Progress bar */}
        <div className="w-64 md:w-72 h-3 bg-gray-800 rounded-full overflow-hidden border border-gray-700 mb-4">
          <div
            className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-150"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Typewriter loading text - left aligned, fixed width container */}
        <div
          className="text-white text-[10px] md:text-sm font-mono h-5 md:h-6 flex items-center text-left"
          style={{
            textShadow: "2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000, 2px 0 0 #000, -2px 0 0 #000, 0 2px 0 #000, 0 -2px 0 #000",
            minWidth: "16rem",
          }}
        >
          {displayedText}
          <span className="animate-pulse">|</span>
        </div>
      </div>
    </div>
  )
})
